import { getAvaliableAccounts } from "../useCase/userUseCase";
import { postCompaniesLogos } from "../useCase/impactedCompanyUseCase";
import {
  readCachedAccountCompanyId,
  writeCachedAccountCompanyId,
} from "./accountCompanyIdCache";
import {
  readCachedCompanyLogo,
  writeCachedCompanyLogo,
} from "./companyLogoCache";
import { normalizeRemoteAssetExpiresAt } from "./remoteAssetExpiry";

const wait = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const isTransientLogoError = (error) => {
  const status = Number(error?.response?.status);
  if (Number.isFinite(status)) {
    if (status === 429) return true;
    if (status >= 500 && status < 600) return true;
    return false;
  }

  const code = String(error?.code || "").toUpperCase();
  if (code === "ECONNABORTED" || code === "ETIMEDOUT" || code === "ERR_NETWORK") {
    return true;
  }

  return !error?.response;
};

const normalizeCompanyId = (value) => {
  if (!value && value !== 0) return null;
  const numeric = Number(String(value).replace(/\D/g, ""));
  if (!numeric || Number.isNaN(numeric)) return null;
  return numeric;
};

const extractCompanyIdFromAccount = (account) => {
  if (!account || typeof account !== "object") return null;
  return (
    account.linkedinCompanyId ??
    account.reference ??
    account.companyId ??
    account.organizationId ??
    account.company_id ??
    null
  );
};

const resolveCompanyIdFromAccounts = (accounts, accountId, accountName) => {
  if (!Array.isArray(accounts) || accounts.length === 0) return null;

  const byId =
    accountId != null
      ? accounts.find((account) => {
          const candidateId =
            account?.accountId ?? account?.value ?? account?.id ?? null;
          return candidateId != null && String(candidateId) === String(accountId);
        })
      : null;

  const byName =
    !byId && accountName
      ? accounts.find((account) => {
          const label =
            account?.label ??
            account?.accountName ??
            account?.name ??
            account?.companyName ??
            "";
          return String(label).trim() === String(accountName).trim();
        })
      : null;

  const candidate = byId || byName;
  return candidate ? extractCompanyIdFromAccount(candidate) : null;
};

export async function warmCompanyLogoCacheForSession({
  accountId,
  accountName,
  linkedinCompanyId,
  prefetchedLogoUrl,
  prefetchedLogoUrlExpiresAt,
  accounts,
  requireLogo = false,
  throwOnFailure = false,
  logoFetchAttempts = 6,
  logoFetchRetryDelayMs = 300,
  allowRemoteCompanyIdLookup = true,
  allowRemoteLogoFetch = true,
}) {
  const normalizedAccountId = accountId ?? null;
  let companyId = normalizeCompanyId(linkedinCompanyId);

  if (!companyId && normalizedAccountId != null) {
    companyId = normalizeCompanyId(
      readCachedAccountCompanyId(normalizedAccountId)
    );
  }

  if (!companyId) {
    companyId = normalizeCompanyId(
      resolveCompanyIdFromAccounts(accounts, normalizedAccountId, accountName)
    );
  }

  if (!companyId && allowRemoteCompanyIdLookup) {
    try {
      const response = await getAvaliableAccounts();
      const accountList = response?.data?.data ?? response?.data ?? [];
      companyId = normalizeCompanyId(
        resolveCompanyIdFromAccounts(
          accountList,
          normalizedAccountId,
          accountName
        )
      );
    } catch {
      // ignore available accounts fallback errors
    }
  }

  if (!companyId) {
    if (requireLogo && throwOnFailure) {
      const missingCompanyIdError = new Error("company_logo_company_id_not_found");
      missingCompanyIdError.code = "COMPANY_LOGO_COMPANY_ID_NOT_FOUND";
      throw missingCompanyIdError;
    }
    return null;
  }

  if (normalizedAccountId != null) {
    writeCachedAccountCompanyId(normalizedAccountId, companyId);
  }

  const normalizedPrefetchedLogoUrl = String(prefetchedLogoUrl || "").trim();
  const normalizedPrefetchedLogoExpiresAt = normalizeRemoteAssetExpiresAt(
    prefetchedLogoUrlExpiresAt
  );
  const hasPrefetchedLogo =
    normalizedPrefetchedLogoUrl.length > 0 &&
    (!normalizedPrefetchedLogoExpiresAt ||
      normalizedPrefetchedLogoExpiresAt > Date.now());
  if (hasPrefetchedLogo) {
    writeCachedCompanyLogo(companyId, {
      logoUrl: normalizedPrefetchedLogoUrl,
      logoUrlExpiresAt: normalizedPrefetchedLogoExpiresAt,
    });
    return companyId;
  }

  const cachedLogo = readCachedCompanyLogo(companyId);
  if (cachedLogo?.logoUrl) return companyId;
  if (!allowRemoteLogoFetch) return companyId;

  const attempts = requireLogo
    ? Math.max(1, Number(logoFetchAttempts) || 1)
    : 1;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      const logoResponse = await postCompaniesLogos({
        organizationIds: [companyId],
      });
      const logos = logoResponse?.logos || [];
      const match = logos.find(
        (logo) => Number(logo.organizationId) === companyId
      );
      if (match?.logoUrl) {
        writeCachedCompanyLogo(companyId, match);
        return companyId;
      }
      // The backend returned successfully but there is no logo for this company.
      // Retrying immediately is unlikely to change outcome.
      break;
    } catch (error) {
      const canRetry = attempt < attempts - 1;
      if (!canRetry) break;
      if (!isTransientLogoError(error)) break;
      await wait(logoFetchRetryDelayMs);
    }
  }

  const refreshedLogo = readCachedCompanyLogo(companyId);
  if (refreshedLogo?.logoUrl) return companyId;

  if (requireLogo && throwOnFailure) {
    const logoNotLoadedError = new Error("company_logo_not_loaded");
    logoNotLoadedError.code = "COMPANY_LOGO_NOT_LOADED";
    throw logoNotLoadedError;
  }

  return companyId;
}
